import { Location } from '@angular/common';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { DOCUMENT, inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { APP_CONFIG } from 'app/core/config/app-config.token';
import { AuthService } from 'app/core/services/auth/auth.service';
import { CustomMsalService } from 'app/core/services/auth/custom-msal.service';
import {
  catchError,
  EMPTY,
  from,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { AUTHENTICATION_CONFIGURATION_NAMES } from '../services/auth/authenticationConfigNames';
import { ConfigService } from '../services/config/config.service';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor
  extends MsalInterceptor
  implements HttpInterceptor {
  private readonly _oktaAuth = inject<OktaAuth>(OKTA_AUTH, { optional: true });
  private readonly configService = inject(ConfigService);
  private customMsalService = inject(CustomMsalService);
  private router = inject(Router);
  private injector = inject(Injector);
  private readonly appConfig = inject(APP_CONFIG);

  constructor() {
    const msalInterceptorConfig = inject<MsalInterceptorConfiguration>(
      MSAL_INTERCEPTOR_CONFIG,
      { optional: true },
    );
    const document = inject<Document>(DOCUMENT);
    const location = inject(Location);
    const msalService = inject(MsalService, { optional: true });
    const msalBroadcastService = inject(MsalBroadcastService, {
      optional: true,
    });

    super(
      msalInterceptorConfig,
      msalService,
      location,
      msalBroadcastService,
      document,
    );
  }

  /**
   * MSAL cannot renew the session silently (refresh token expired and
   * the hidden-iframe fallback needs third-party cookies the browser
   * blocks). Sending the request without a token would just produce a
   * 401, so abort it and send the user to the login page to
   * re-authenticate interactively.
   */
  private redirectToInteractiveLogin(): Observable<never> {
    // AuthService is resolved lazily to avoid a DI cycle
    // (interceptor -> AuthService -> HttpClient -> interceptor).
    this.injector.get(AuthService).clearLocalAuthState();
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.router.url },
    });
    return EMPTY;
  }

  /**
   * Handles a failed silent token acquisition. The session genuinely needs
   * an interactive re-auth only when MSAL says so: `InteractionRequiredAuthError`,
   * or a `BrowserAuthError` such as the hidden-iframe timeout produced when
   * third-party cookies are blocked (Safari ITP / tracking protection). In
   * those cases we abort and send the user to login (returns EMPTY).
   *
   * For any other (typically transient) failure we do NOT force a logout -
   * we fall back to the original behaviour of letting the request proceed
   * without a freshly-acquired token (signalled by emitting `null`); it will
   * 401 and be handled downstream if the session really is dead.
   */
  private handleSilentAcquisitionFailure(
    label: string,
    err: unknown,
  ): Observable<null> {
    if (err instanceof InteractionRequiredAuthError) {
      console.error(`${label} session requires interactive login:`, err);
      return this.redirectToInteractiveLogin();
    }
    if (err instanceof BrowserAuthError) {
      console.error(
        `${label} silent renewal could not complete (likely blocked third-party cookies):`,
        err,
      );
      return this.redirectToInteractiveLogin();
    }
    console.error(
      `${label} silent token acquisition failed; sending the request without a refreshed token:`,
      err,
    );
    return of(null);
  }

  /**
   * Whether the request is bound for our own API — the test that gates both
   * token attachment and expired-session handling.
   *
   * An empty `serverUrl` must never match. It is the build-time fallback when
   * `/api/config` is unreachable, and `String.includes('')` is true for every
   * URL, which would attach our bearer token to third-party requests and treat
   * any 401 as a dead session.
   */
  private isApiRequest(request: HttpRequest<unknown>): boolean {
    const { serverUrl } = this.appConfig;
    return (
      (serverUrl !== '' && request.url.includes(serverUrl)) ||
      request.url.includes(':7044')
    );
  }

  /**
   * Global safety net for a session that lapses mid-use. On a 401 from the
   * API, force re-authentication only when our own credential is actually
   * stale; a 401 with a still-valid credential is a per-endpoint access
   * decision and is left for the caller to handle (e.g. loggedInUser routes
   * it to NOT_CUSTOMER). Non-API responses pass through untouched.
   */
  private guardAgainstExpiredSession(
    request: HttpRequest<unknown>,
    stream: Observable<HttpEvent<unknown>>,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isApiRequest(request)) {
      return stream;
    }
    return stream.pipe(
      catchError((err) => {
        const authService = this.injector.get(AuthService);
        // Tear down a session only when it was actually live, only once, and
        // never while we are already heading to the login page. The first
        // matching 401 clears auth state (isAuthenticated -> false), so the
        // concurrent and follow-on 401s that background calls (e.g. the build
        // versions / keepalive ping) produce become no-ops rather than a
        // redirect storm.
        if (
          err?.status === 401 &&
          authService.isAuthenticated &&
          !this.router.url.startsWith('/login') &&
          authService.isLocalCredentialStale()
        ) {
          return this.redirectToInteractiveLogin();
        }
        return throwError(() => err);
      }),
    );
  }

  override intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    let req = request;

    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA] &&
      localStorage.getItem('esqep.authType') ===
      AUTHENTICATION_CONFIGURATION_NAMES.OKTA
    ) {
      return this.guardAgainstExpiredSession(
        request,
        next.handle(this.addAuthHeaderToAllowedOrigins(request)),
      );
    }
    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.CSOD] &&
      localStorage.getItem('esqep.authType') ===
      AUTHENTICATION_CONFIGURATION_NAMES.CSOD
    ) {
      const authToken = localStorage.getItem('esqep.auth.saml');

      if (authToken) {
        req = request.clone({
          setHeaders: {
            Authorization: `Bearer ${authToken}`,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });
      }
      return this.guardAgainstExpiredSession(req, next.handle(req));
    }

    if (
      (this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA] &&
        localStorage.getItem('esqep.authType') ===
        AUTHENTICATION_CONFIGURATION_NAMES.ENTRA) ||
      (this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.B2C] &&
        localStorage.getItem('esqep.authType') ===
        AUTHENTICATION_CONFIGURATION_NAMES.B2C)
    ) {
      const isB2C =
        localStorage.getItem('esqep.authType') ===
        AUTHENTICATION_CONFIGURATION_NAMES.B2C;

      if (isB2C && this.isApiRequest(request)) {
        const account =
          this.customMsalService.instance.getActiveAccount() ||
          this.customMsalService.instance.getAllAccounts()[0];

        if (account) {
          const b2cConfig =
            this.configService.authSetup[
            AUTHENTICATION_CONFIGURATION_NAMES.B2C
            ];
          const b2cAuthority = `https://${b2cConfig.authorityDomain}/${b2cConfig.tenantId}`;
          return from(
            this.customMsalService.instance.acquireTokenSilent({
              scopes: [b2cConfig.clientId + '/.default'],
              account: account,
              authority: b2cAuthority,
            }),
          ).pipe(
            // catchError is placed BEFORE switchMap so it only handles a
            // failed token acquisition; a server error from the request
            // itself flows through guardAgainstExpiredSession instead.
            catchError((err) =>
              this.handleSilentAcquisitionFailure('B2C CIAM', err),
            ),
            switchMap((result) => {
              // result === null means silent acquisition hit a transient error
              // and we fall back to sending the request without a refreshed token.
              const outgoing = result
                ? request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${result.accessToken}`,
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                  },
                })
                : request;
              return this.guardAgainstExpiredSession(
                request,
                next.handle(outgoing),
              );
            }),
          );
        } else {
          // console.warn(
          //   'B2C Interceptor: No active account found for request',
          //   request.url,
          // );
        }
      } else if (!isB2C && this.isApiRequest(request)) {
        const account =
          this.customMsalService.instance.getActiveAccount() ||
          this.customMsalService.instance.getAllAccounts()[0];
        if (account) {
          const entraConfig =
            this.configService.authSetup[
            AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
            ];
          return from(
            this.customMsalService.instance.acquireTokenSilent({
              scopes: [entraConfig.clientId + '/.default'],
              account: account,
              authority: entraConfig.authority,
            }),
          ).pipe(
            // catchError is placed BEFORE switchMap so it only handles a
            // failed token acquisition; a server error from the request
            // itself flows through guardAgainstExpiredSession instead.
            catchError((err) =>
              this.handleSilentAcquisitionFailure('ENTRA', err),
            ),
            switchMap((result) => {
              // result === null means silent acquisition hit a transient error
              // and we fall back to sending the request without a refreshed token.
              const outgoing = result
                ? request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${result.accessToken}`,
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                  },
                })
                : request;
              return this.guardAgainstExpiredSession(
                request,
                next.handle(outgoing),
              );
            }),
          );
        }
      }

      req = request.clone({
        setHeaders: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      return this.guardAgainstExpiredSession(req, next.handle(req));
    }
    return this.guardAgainstExpiredSession(req, next.handle(req));
  }

  private addAuthHeaderToAllowedOrigins(
    request: HttpRequest<unknown>,
  ): HttpRequest<unknown> {
    let req = request;
    if (
      !!this.configService.authSetup[
        AUTHENTICATION_CONFIGURATION_NAMES.OKTA
      ].allowedOrigins.find((origin) => request.url.includes(origin))
    ) {
      const authToken = this._oktaAuth.getAccessToken();
      req = request.clone({
        setHeaders: {
          Authorization: `Bearer ${authToken}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
    }

    return req;
  }
}
